// Backoffice Tageskasse-Dashboard: Soll/Ist/Diff je Fahrer

import { useState } from "react";
import { useCashSummaryByDriver, useHandovers } from "@/lib/cash";
import { supabase } from "@/lib/supabase";

const fmt = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

export default function BackofficeKassePage() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const { data: summary = [], isLoading } = useCashSummaryByDriver(date);
  const { data: handovers = [] } = useHandovers(date);

  async function downloadDatev() {
    const dayStart = `${date}T00:00:00Z`;
    const dayEnd = `${date}T23:59:59Z`;
    const { data } = await supabase
      .from("payments")
      .select(`
        amount_cents, status, paid_at, external_ref,
        order:orders(order_number, customer:customers(display_name))
      `)
      .eq("method", "cash")
      .gte("paid_at", dayStart)
      .lte("paid_at", dayEnd)
      .order("paid_at");
    const header = "Datum;Belegnummer;Kunde;Betrag;Status";
    const rows = (data ?? []).map((p) => {
      const ord = Array.isArray(p.order) ? p.order[0] : p.order;
      const cust = ord?.customer ? (Array.isArray(ord.customer) ? ord.customer[0] : ord.customer) : null;
      return [
        new Date(p.paid_at ?? "").toLocaleDateString("de-DE"),
        ord?.order_number ?? "",
        cust?.display_name ?? "",
        ((p.amount_cents / 100).toFixed(2)).replace(".", ","),
        p.status,
      ].join(";");
    });
    const csv = "﻿" + [header, ...rows].join("\r\n"); // BOM + Windows-Zeilen
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hofsirges-kassenbuch-${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-bold text-hof-900">Tageskasse</h1>
        <div className="flex items-center gap-2">
          <input type="date" className="input max-w-xs" value={date} onChange={(e) => setDate(e.target.value)} />
          <button onClick={downloadDatev} className="btn-secondary">⬇ CSV-Export</button>
        </div>
      </div>

      <section className="card">
        <h2 className="font-semibold text-hof-800 mb-3">Soll/Ist je Fahrer</h2>
        {isLoading ? (
          <p className="text-hof-600">Lade…</p>
        ) : summary.length === 0 ? (
          <p className="text-hof-600 text-center py-4">Keine Bar-Annahmen an diesem Tag.</p>
        ) : (
          <table className="w-full text-left">
            <thead className="text-sm text-hof-600 border-b border-hof-100">
              <tr>
                <th className="py-2">Fahrer</th>
                <th className="py-2 text-right">Soll (gesamt)</th>
                <th className="py-2 text-right">Pending</th>
                <th className="py-2 text-right">Übergeben</th>
                <th className="py-2 text-right">Diff</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((s) => {
                const handover = handovers.find((h) => h.driver_id === s.driver_id);
                const diff = (handover?.diff_cents ?? 0);
                return (
                  <tr key={s.driver_id} className="border-b border-hof-100">
                    <td className="py-3 font-medium">{s.name}</td>
                    <td className="py-3 text-right font-mono">{fmt.format(s.soll / 100)}</td>
                    <td className="py-3 text-right font-mono text-sonne-700">
                      {s.pending > 0 ? fmt.format(s.pending / 100) : "—"}
                    </td>
                    <td className="py-3 text-right font-mono text-wiese-700">
                      {s.uebergeben > 0 ? fmt.format(s.uebergeben / 100) : "—"}
                    </td>
                    <td className={`py-3 text-right font-mono ${diff === 0 ? "" : diff < 0 ? "text-red-700" : "text-sonne-700"}`}>
                      {handover ? fmt.format(diff / 100) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <section className="card">
        <h2 className="font-semibold text-hof-800 mb-3">Übergaben des Tages</h2>
        {handovers.length === 0 ? (
          <p className="text-hof-600 text-center py-2">Noch keine Übergaben.</p>
        ) : (
          <div className="space-y-2">
            {handovers.map((h) => (
              <div key={h.id} className="flex items-center justify-between py-2 border-b border-hof-100 last:border-b-0">
                <div>
                  <div className="font-medium">{h.driver?.full_name ?? h.driver_id.slice(0, 8)}</div>
                  <div className="text-xs text-hof-500">
                    {new Date(h.handed_over_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                    {h.note && ` · ${h.note}`}
                  </div>
                </div>
                <div className="text-right font-mono">
                  <span className="text-hof-500 text-sm">Soll {fmt.format(h.soll_cents / 100)} ·</span>
                  <span className="ml-2 font-bold">Ist {fmt.format(h.ist_cents / 100)}</span>
                  {h.diff_cents !== 0 && (
                    <span className={`ml-2 ${h.diff_cents < 0 ? "text-red-700" : "text-sonne-700"}`}>
                      ({h.diff_cents > 0 ? "+" : ""}{fmt.format(h.diff_cents / 100)})
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
